import React, { useState } from 'react';
import { ArrowRight, Gauge, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { products } from '../data/mock';

const ProductsSection = () => {
  const [activeProduct, setActiveProduct] = useState(products[0]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="section-padding bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block text-[#B8956B] font-medium text-sm uppercase tracking-[0.2em] mb-4">
            Our Products
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#252A32] mb-6 heading-display">
            Elevator Solutions for Every <span className="text-[#B8956B] italic">Need</span>
          </h2>
          <div className="elegant-divider mx-auto mb-6" />
          <p className="text-[#404752] text-lg">
            From compact home lifts to heavy-duty industrial elevators, we offer a comprehensive 
            range of customized solutions.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {products.map((product) => (
            <div 
              key={product.id}
              className={`product-card bg-white rounded-2xl overflow-hidden border transition-all cursor-pointer ${
                activeProduct.id === product.id 
                  ? 'border-[#B8956B] shadow-xl' 
                  : 'border-gray-100 hover:border-gray-200'
              }`}
              onClick={() => setActiveProduct(product)}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#252A32]/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                  <h3 className="text-white font-semibold text-xl">{product.name}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-[#404752] text-sm mb-4 line-clamp-2">{product.description}</p>
                
                {/* Specs */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-[#B8956B]" />
                    <span className="text-[#404752]">{product.capacity}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Gauge className="w-4 h-4 text-[#B8956B]" />
                    <span className="text-[#404752]">{product.speed}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {product.features.slice(0, 3).map((feature, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary"
                      className="bg-[#FDFBF7] text-[#404752] text-xs font-medium border-0"
                    >
                      {feature}
                    </Badge>
                  ))}
                  {product.features.length > 3 && (
                    <Badge 
                      variant="secondary"
                      className="bg-[#B8956B]/10 text-[#B8956B] text-xs font-medium border-0"
                    >
                      +{product.features.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Featured Product Detail */}
        <div className="bg-[#252A32] rounded-3xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="p-10 lg:p-14">
              <Badge className="bg-[#B8956B] text-white mb-6 font-medium tracking-wide">Featured Product</Badge>
              <h3 className="text-3xl lg:text-4xl font-semibold text-white mb-5 heading-display">{activeProduct.name}</h3>
              <p className="text-gray-400 mb-8 leading-relaxed">{activeProduct.description}</p>
              
              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-[#B8956B] text-xs font-medium uppercase tracking-wider mb-1">Capacity</p>
                  <p className="text-white text-lg font-semibold">{activeProduct.capacity}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-[#B8956B] text-xs font-medium uppercase tracking-wider mb-1">Speed</p>
                  <p className="text-white text-lg font-semibold">{activeProduct.speed}</p>
                </div>
              </div>

              <div className="mb-10">
                <p className="text-[#B8956B] text-xs font-medium uppercase tracking-wider mb-4">Key Features</p>
                <div className="flex flex-wrap gap-2">
                  {activeProduct.features.map((feature, idx) => (
                    <Badge 
                      key={idx}
                      className="bg-white/5 text-white border border-white/10 font-normal"
                    >
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>

              <Button 
                onClick={scrollToContact}
                className="btn-gold text-white font-medium group border-0 tracking-wide"
              >
                Request Quote
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="relative h-72 lg:h-auto">
              <img 
                src={activeProduct.image}
                alt={activeProduct.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#252A32] via-transparent to-transparent lg:block hidden" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
